import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { PaletteMode } from "@mui/material";

// Constants
export const THEME_MODE_STORAGE_KEY = "user_theme_mode_preference";

// Types
interface ThemeModeContextType {
  mode: PaletteMode;
  toggleMode: () => void;
  setMode: (mode: PaletteMode) => void;
}

// Resolve the initial mode: stored preference first, then always "light".
// OS prefers-color-scheme is intentionally ignored so the app always opens
// in Light Mode for first-time visitors regardless of system setting.
export const getInitialMode = (): PaletteMode => {
  try {
    const stored = localStorage.getItem(THEME_MODE_STORAGE_KEY);
    if (stored === "light" || stored === "dark") {
      return stored;
    }
  } catch {
    // localStorage unavailable; default to light for this session
  }
  return "light";
};

// Create context
const ThemeModeContext = createContext<ThemeModeContextType | undefined>(
  undefined,
);

// Provider component
export const ThemeModeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [mode, setModeState] = useState<PaletteMode>(getInitialMode);

  // Persist the preference and sync the SCSS data-theme hook
  useEffect(() => {
    try {
      localStorage.setItem(THEME_MODE_STORAGE_KEY, mode);
    } catch {
      // localStorage unavailable; mode still applies for this session
    }
    document.documentElement.setAttribute("data-theme", mode);
  }, [mode]);

  const setMode = useCallback((next: PaletteMode) => {
    setModeState(next);
  }, []);

  const toggleMode = useCallback(() => {
    setModeState((prev) => (prev === "light" ? "dark" : "light"));
  }, []);

  return (
    <ThemeModeContext.Provider value={{ mode, toggleMode, setMode }}>
      {children}
    </ThemeModeContext.Provider>
  );
};

// Custom hook
export const useThemeMode = (): ThemeModeContextType => {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error("useThemeMode must be used within a ThemeModeProvider");
  }
  return context;
};
